import type { PermissionProps } from "@qendo/permissions";
import {
    type MultiListData,
    type OptionData,
    type OptionGroup,
    OptionType,
    type OptionTypeData,
    type OtherableOptionStore,
    type OtherableOptionType,
    OtherableOptionTypes,
} from "@qendo/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { createContext, type ReactNode, useCallback, useContext, useMemo } from "react";
import { useApi } from "~/providers/ApiProvider";
import { useSystemOptions } from "~/providers/SystemOptionsProvider";
import { isOtherableOption } from "~/util/qendo/options";
import { optionPermissionSection } from "~/util/qendo/permissions";

declare interface OptionPayload<T extends OtherableOptionType = OtherableOptionType> {
    type: T;
    title: string;
    group?: OptionGroup;
}

declare interface StoreOptionResponse<T extends OtherableOptionType = OtherableOptionType> {
    created: boolean;
    type: T;
    option: OptionTypeData<T>;
}

export type OptionFinder = <T extends OptionType = OptionType>(type: T, id?: string | null) => OptionTypeData<T> | null;
export type OptionLister = <T extends OptionType = OptionType>(type: T) => OptionTypeData<T>[];

declare interface OptionState {
    listOptions: OptionLister;
    findOption: OptionFinder;
    canMakeOption(type: OptionType): type is OtherableOptionType;
    storeOption(payload: OptionPayload): Promise<string | null>;
}

export const EmptyOtherableOptions = Object.fromEntries(
    Object.values(OtherableOptionTypes).map(<T extends OtherableOptionType>(k: T) => [k, [] as OptionTypeData<T>[]]),
) as OtherableOptionStore;

export const OptionContext = createContext<OptionState>({
    listOptions: (_type) => [],
    findOption: (_type, _id) => null,
    canMakeOption: (_type): _type is OtherableOptionType => false,
    storeOption: async (_payload) => null,
});

export function useOptions() {
    return useContext(OptionContext);
}

export type OptionsProviderProps = {
    profile_id: string | null;
    children: ReactNode;
} & PermissionProps;

export function OptionsProvider({ profile_id, can, children }: OptionsProviderProps) {
    const queryClient = useQueryClient();
    const { client } = useApi();
    const { options: systemOptions } = useSystemOptions();

    const queryKey = ["options", profile_id];

    const profileQuery = useQuery({
        queryKey,
        queryFn: async () => {
            if (profile_id) {
                return await client()
                    .get(`profiles/${profile_id}/options`)
                    .then((response: AxiosResponse<MultiListData<OptionType, OptionTypeData>>) => {
                        return { ...EmptyOtherableOptions, ...response.data.data };
                    })
                    .catch(() => EmptyOtherableOptions);
            }
            return EmptyOtherableOptions;
        },
        staleTime: 1000 * 60 * 60, // 1 hour
        placeholderData: EmptyOtherableOptions,
        refetchOnMount: true,
    });

    const canMakeOption = useCallback(
        (type: OptionType): type is OtherableOptionType => {
            return isOtherableOption(type) && can("update", optionPermissionSection(type));
        },
        [can],
    );

    const storeOptionMutation = useMutation({
        mutationFn: async ({ type, title, group }: OptionPayload) => {
            const payload = [OptionType.calendar_events, OptionType.team_members].includes(type)
                ? { title, group }
                : { title };

            return await client()
                .post(`profiles/${profile_id}/options/${type}`, payload)
                .then((response: AxiosResponse<OptionData>): StoreOptionResponse => {
                    return {
                        created: response.status === 201,
                        type,
                        option: response.data,
                    };
                });
        },
        onSuccess: ({ created, type, option }: StoreOptionResponse) => {
            if (!created) {
                return;
            }

            queryClient.setQueryData(queryKey, (cache?: OtherableOptionStore) => {
                const current = cache ?? EmptyOtherableOptions;

                return {
                    ...current,
                    [type]: [option, ...current[type]],
                };
            });
        },
    });

    const storeOption = async (payload: OptionPayload) => {
        const { type } = payload;

        if (!canMakeOption(type)) {
            return null;
        }

        return await storeOptionMutation.mutateAsync(payload).then(({ option }: StoreOptionResponse) => {
            return option.id;
        });
    };

    const profileOptions = useMemo(
        () => (profile_id ? (profileQuery.data ?? EmptyOtherableOptions) : EmptyOtherableOptions),
        [profile_id, profileQuery],
    );

    const listOptions: OptionLister = useCallback(
        // @ts-expect-error
        (type) => [...(systemOptions[type] ?? []), ...(profileOptions[type] ?? [])],
        [systemOptions, profileOptions],
    );

    const findOption: OptionFinder = useCallback(
        (type, id) => (id ? listOptions(type).find((option) => option.id === id) : null) ?? null,
        [listOptions],
    );

    const value = {
        listOptions,
        findOption,
        canMakeOption,
        storeOption,
    };

    return <OptionContext.Provider value={value}>{children}</OptionContext.Provider>;
}
